import { LocalDate } from "@js-joda/core";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { MonthPeriodNavigator } from "components/designV2/periodNavigator/monthPeriodNavigator.component";
import { Text } from "components/designV2/text/text";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import { ColorV2 } from "config/colors";
import type { CompanyBankAccountType } from "model/Company";
import { Api } from "services/api/api.service";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import {
  HeaderRightAligned,
  IndividualSalaryFilters,
  PeriodNavigatorContainer,
  SalaryPeriodCard,
  SalaryPageContainer,
} from "./salary.styled";
import { FileViewer } from "../fileViewer/fileViewer.component";
import { AGIList } from "./agiList.component";
import { SalaryPayoutPanel } from "./salaryPayoutPanel.component";
import { useSalaryPayoutPanel } from "./useSalaryPayoutPanel";

const getCompanySettingValue = (
  settings: Array<{ key: string; value: string }> | undefined,
  key: string,
): string => {
  return settings?.find((setting) => setting.key === key)?.value ?? "";
};

export const AGIOverview = (): JSX.Element => {
  const [currentDate, setCurrentDate] = useState(
    LocalDate.now().minusMonths(1),
  );
  const [companyState] = useCompanyStore();
  const company = companyState.company;
  const { t } = useTranslation();
  const salaryPayoutPanel = useSalaryPayoutPanel({
    companyId: company?.id,
    currentDate,
  });
  const bankAgreementId = getCompanySettingValue(
    companyState.companySettings,
    "CompanyBankAgreementId",
  );
  const bankAccountValue = getCompanySettingValue(
    companyState.companySettings,
    "CompanyBankAccountValue",
  );
  const bankAccountTypeValue = getCompanySettingValue(
    companyState.companySettings,
    "CompanyBankAccountType",
  );
  const bankAccountType: CompanyBankAccountType =
    bankAccountTypeValue === "InternationalBankAccount"
      ? "InternationalBankAccount"
      : "BankAccount";

  const fetchAGIFile = useCallback(() => {
    if (!company) {
      return Promise.reject(new Error("Missing company"));
    }

    return Api().company.salary.getAGI(
      company.id,
      currentDate.year(),
      currentDate.monthValue(),
    );
  }, [company, currentDate]);

  return (
    <SalaryPageContainer>
      <HeaderRightAligned>
        <TooltipHover text={t("Salary.AGIInfo")} arrow="up">
          <Text typography="s1" color={ColorV2.Blue1}>
            {t("Salary.WhatIs", { name: t("Salary.AGI").toLowerCase() })}
          </Text>
        </TooltipHover>
      </HeaderRightAligned>
      <SalaryPeriodCard>
        <IndividualSalaryFilters>
          <PeriodNavigatorContainer>
            <MonthPeriodNavigator
              currentDate={currentDate}
              onChange={setCurrentDate}
              showTodayButton
            />
          </PeriodNavigatorContainer>
        </IndividualSalaryFilters>
      </SalaryPeriodCard>

      {company && (
        <FileViewer
          fetchEndpoint={fetchAGIFile}
          filename={`AGI_${currentDate.year()}_${currentDate.monthValue()}.xml`}
          type="application/xml"
          currentDate={currentDate}
          extraActions={
            <PrimaryButton
              label={t("Salary.PayOutSalaries")}
              size="small"
              onClick={salaryPayoutPanel.openPanel}
            />
          }
          ListComponent={AGIList}
        />
      )}
      <SalaryPayoutPanel
        bankAgreementId={bankAgreementId}
        bankAccountType={bankAccountType}
        bankAccountValue={bankAccountValue}
        canCreatePayoutFile={salaryPayoutPanel.canCreatePayoutFile}
        currentDate={currentDate}
        excludedWorkerCount={salaryPayoutPanel.excludedWorkerCount}
        isCreatingPayoutFile={salaryPayoutPanel.isCreatingPayoutFile}
        isOpen={salaryPayoutPanel.isOpen}
        payoutDate={salaryPayoutPanel.payoutDate}
        payoutWorkerCount={salaryPayoutPanel.payoutWorkerCount}
        previewState={salaryPayoutPanel.previewState}
        totalNetPayoutAmount={salaryPayoutPanel.totalNetPayoutAmount}
        onClose={salaryPayoutPanel.closePanel}
        onCreate={salaryPayoutPanel.createPayoutFile}
        onPayoutDateChange={salaryPayoutPanel.setPayoutDate}
      />
    </SalaryPageContainer>
  );
};
